import { apolloQueryWithAbort, EnumFetchPolicy, SERVICES } from '@/app/client-appolo';
import sdkPaymentsService, {
  BankFragment,
  BankType,
  BudgetClassificationCodeFragment,
  BudgetPaymentInput,
  CalculateExoticAmountInput,
  CommissionInput,
  LegalPartyIdInput,
  Operations,
  OperationTypeCategoryFragment,
  PageInput,
  PaymentIdListInput,
  PaymentSubscriptionStateEnum,
  PersonalQrOperationFilterInput,
  QrOperationFilterInput,
  QrOperationLast2DaysFilter,
  QrTransferFilterInput,
  QrTransferInput,
  RefundInput,
  RefundIsAvailableInput,
  SalaryIdInput,
  SalaryInfoWithDetailForSignFragment,
  SalaryRequest,
  SearchSalary,
  SignatureInput,
  SignatureSalaryInput,
  Status,
  TransferTemplateCriteriaInput,
  UpdatePayment,
} from '@/app/graphql/payment/payment';
import {
  FindTransferByInvoiceDetailByIdDocument,
  type FindTransferByInvoiceDetailByIdQuery,
} from '@/app/dev-utils/payment.sdk';
import {
  CardFormInput,
  ConversionFormInput,
  InterBankFormInput,
  IntraBankFormInput,
  ListenPayment,
  ListenSalaryStatement,
  OperationHistory,
  PaginatedSalaryStatement,
  PaymentForm,
  QrPaymentForm,
  SalaryInfoWithDetail,
  SearchPaymentByCriteriaParams,
  SwiftFormInput,
} from './types';
import {
  checkSystemError,
  excludeRestrictedCountries,
  excludeStandardCurrencies,
  findBudgetPaymentOperation,
  findConversionOperation,
  findSwiftOperation,
  formatListenPayment,
  formattedModelProcess,
  mapBanksByCode,
  mapBudgetClassificationByCodeCriteria,
  mapCardTransfer,
  mapConversion,
  mapEditTransferTemplate,
  mapFilterQrOperationInstallment,
  mapFilterQrTransaction,
  mapInterBankTransferFormToInput,
  mapInterBankTransferOperations,
  mapIntraBankOperation,
  mapIntraBankTransfer,
  mapOperationTypeCategoryByCodeCriteria,
  mapPayDaInstallmentOperations,
  mapPaymentBudgetData,
  mapPaymentByCriteria,
  mapPaymentConversionData,
  mapPaymentQrData,
  mapPaymentQrRefundedData,
  mapPaymentRko2CardTransferData,
  mapPaymentStatusHistoryByPaymentId,
  mapPaymentSwiftData,
  mapPaymentTransferByInvoiceData,
  mapPaymentTransferData,
  mapQrPaymentFormToQrTransferInput,
  mapRko2CardOperations,
  mapSalaries,
  mapSalary,
  mapSalaryDetail,
  mapSignatories,
  mapSwift,
  mapTransferTemplate,
  standardCurrencies,
} from './helps';

import { User } from '@/app/stores/user/types';
import { useAuthUserStore } from '@/app/stores/user';
import { Currencies } from '@/shared/services/account';
import { useHistoryTransactionStore } from '@/app/stores/history-transaction';
import { TimeRuleDto } from '@/app/graphql/rule-engine/sdk.rule-engine';
import {
  PayControlKeyAvailabilityFragment,
  SignRight,
  SignType,
} from '@/app/graphql/access-control/sdk.access-control';

import { usePaymentStore } from '@/app/stores/payment';
import { formattedCurrencyRates } from '@/shared/services/payment';
import { Subject } from 'rxjs';
import { InstallmentFilter } from '@/shared/services/installment';

const pageable = { amountElements: 40, currentPage: 1 };
export const userListenSalaryStatementLine: Subject<ListenSalaryStatement> = new Subject();

export async function saveInterBankTransferNativeDraft(interBankTransfer: InterBankFormInput) {
  const input = mapInterBankTransferFormToInput(interBankTransfer);
  const response = await sdkPaymentsService.saveInterBankTransferNativeDraft({ interBankTransfer: input });
  return checkSystemError(response.saveInterBankTransferNativeDraft.paymentId);
}

export async function saveIntraBankTransferDraft(intraBankTransfer: IntraBankFormInput) {
  const intraTransfer = mapIntraBankTransfer(intraBankTransfer);
  const isOwnTransfer = intraBankTransfer.operation?.operationCode === Operations.IntrabankTransferOwn;

  if (isOwnTransfer) {
    const response = await sdkPaymentsService.saveIntraBankOwnerTransferDraft({ intraTransfer });
    return checkSystemError(response.saveIntraBankOwnerTransferDraft.paymentId);
  }

  const response = await sdkPaymentsService.saveIntraBankForeignTransferDraft({ intraTransfer });
  return checkSystemError(response.saveIntraBankForeignTransferDraft.paymentId);
}

export async function saveConversionDraft(conversionInput: ConversionFormInput) {
  const conversion = mapConversion(conversionInput);
  const response = await sdkPaymentsService.saveConversionDraft({ conversion });
  return checkSystemError(response.saveConversionDraft.paymentId);
}

export async function saveCardTransferDraft(ownCardTransfer: CardFormInput) {
  const cardTransfer = mapCardTransfer(ownCardTransfer);
  const response = await sdkPaymentsService.saveCardTransfer({ cardTransfer });
  return checkSystemError(response.saveCardTransfer.paymentId);
}

export async function saveSwift(swiftInput: SwiftFormInput) {
  const swift = mapSwift(swiftInput);
  const response = await sdkPaymentsService.saveSwift({ swift });
  return checkSystemError(response.saveSwift.paymentId);
}

export async function saveQrTransfer(form: QrPaymentForm) {
  const qrTransferInput = mapQrPaymentFormToQrTransferInput(form);
  const response = await sdkPaymentsService.saveQrTransfer({ qrTransferInput });
  return checkSystemError(response.saveQrTransfer.paymentId);
}

export async function saveQrTransferDraft(qrTransferInput: QrTransferInput) {
  const response = await sdkPaymentsService.saveQrTransfer({ qrTransferInput });
  return {
    id: checkSystemError(response.saveQrTransfer.paymentId)?.paymentId || 0,
    pcTransactionId: response.saveQrTransfer.pcTransactionId,
  };
}

export async function updateEditedPayment(payment: PaymentForm, id: number) {
  const operationCode = payment.operation?.operationCode as Operations;
  const interBankOperations = [Operations.Gross, Operations.Clearing];
  const intraBankOperations = [Operations.IntrabankTransferForeign, Operations.IntrabankTransferOwn];
  const transferOperations = [...interBankOperations, ...intraBankOperations];
  const cardOperations = [
    Operations.Rko_2BusinessCardOwn,
    Operations.Rko_2BusinessCardForeign,
    Operations.Rko_2IndividualCardOwn,
    Operations.Rko_2IndividualCardForeign,
    Operations.BusinessCard_2RkoOwn,
    Operations.BusinessCard_2RkoForeign,
  ];

  const mapTransferData = interBankOperations.includes(operationCode)
    ? mapInterBankTransferFormToInput
    : intraBankOperations.includes(operationCode)
      ? mapIntraBankTransfer
      : () => null;

  const updatePayment: UpdatePayment = {
    id,
    updateSwiftInput: operationCode === Operations.Swift ? mapSwift(payment as SwiftFormInput) : null,
    updateCardTransferInput: cardOperations.includes(operationCode) ? mapCardTransfer(payment as CardFormInput) : null,
    updateTransferInput: transferOperations.includes(operationCode)
      ? mapTransferData(payment as InterBankFormInput)
      : null,
    updateTransferByInvoice: null,
  };

  const response = await sdkPaymentsService.updatePayment({ updatePayment });
  return response.updatePayment;
}

export async function updateEditedTransferByInvoice(qrTransferInput: QrTransferInput, paymentId: number) {
  const updatePayment: UpdatePayment = {
    id: paymentId,
    updateCardTransferInput: null,
    updateSwiftInput: null,
    updateTransferByInvoice: qrTransferInput,
    updateTransferInput: null,
  };

  const response = await sdkPaymentsService.updatePayment({ updatePayment });
  return response.updatePayment;
}

export async function getAllOperations() {
  const response = await sdkPaymentsService.getAllOperation();
  return response.getAllOperation;
}

export async function getAllCountry() {
  const response = await sdkPaymentsService.getAllCountry();
  return response.getAllCountry;
}

export async function findTransferDataByPaymentIdAndUser(
  paymentId: number,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
) {
  if (!user.currentContract) return;
  const payment = {
    legalPartyId: user.currentContract.orgReferenceId,
    paymentId,
  };
  const response = await sdkPaymentsService.findTransferDataByPaymentId({ payment });
  return mapPaymentTransferData(response.findTransferDataByPaymentId, user, timeRule, keyAvailability);
}

export async function findTransferDataByPaymentId(paymentId: number, legalPartyId: number) {
  const payment = { paymentId, legalPartyId };
  const response = await sdkPaymentsService.findTransferDataByPaymentId({ payment });
  return response.findTransferDataByPaymentId;
}

export async function findTransferByInvoiceDetailById(paymentIdInput: { legalPartyId: number; paymentId: number }) {
  const response = await apolloQueryWithAbort(SERVICES.PAYMENT_SERVICE, FindTransferByInvoiceDetailByIdDocument, {
    variables: { paymentIdInput },
    fetchPolicy: EnumFetchPolicy.NO_CACHE,
  });
  return (response.data as FindTransferByInvoiceDetailByIdQuery).findTransferByInvoiceDetailById;
}

export async function findTransferByInvoiceDetailByIdAndUser(
  paymentId: number,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
) {
  if (!user.currentContract) return;
  const paymentIdInput = {
    legalPartyId: user.currentContract.orgReferenceId,
    paymentId,
  };
  const response = await apolloQueryWithAbort(SERVICES.PAYMENT_SERVICE, FindTransferByInvoiceDetailByIdDocument, {
    variables: { paymentIdInput },
    fetchPolicy: EnumFetchPolicy.NO_CACHE,
  });
  return mapPaymentTransferByInvoiceData(
    (response.data as FindTransferByInvoiceDetailByIdQuery).findTransferByInvoiceDetailById,
    user,
    timeRule,
    keyAvailability
  );
}

export async function findConversionDataByPaymentId(paymentId: number, legalPartyId: number) {
  const payment = { paymentId, legalPartyId };
  const response = await sdkPaymentsService.findConversionDataByPaymentId({ payment });
  return response.findConversionDataByPaymentId;
}

export async function findCardTransferDataByPaymentId(paymentId: number, legalPartyId: number) {
  const payment = { paymentId, legalPartyId };
  const response = await sdkPaymentsService.findCardTransferDataByPaymentId({ payment });
  return response.findCardTransferDataByPaymentId;
}

export async function findCardTransferDataByPaymentIdAndUser(
  paymentId: number,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
) {
  if (!user.currentContract) return;
  const payment = {
    legalPartyId: user.currentContract.orgReferenceId,
    paymentId,
  };
  const response = await sdkPaymentsService.findCardTransferDataByPaymentId({ payment });
  return mapPaymentRko2CardTransferData(response.findCardTransferDataByPaymentId, user, timeRule, keyAvailability);
}

export async function findConversionDataByPaymentIdAndUser(
  paymentId: number,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
) {
  if (!user.currentContract) return;
  const payment = {
    legalPartyId: user.currentContract.orgReferenceId,
    paymentId,
  };
  const response = await sdkPaymentsService.findConversionDataByPaymentId({ payment });
  return mapPaymentConversionData(response.findConversionDataByPaymentId, user, timeRule, keyAvailability);
}

export async function findSwiftDataByPaymentId(paymentId: number, legalPartyId: number) {
  const payment = { paymentId, legalPartyId };
  const response = await sdkPaymentsService.findSwiftDataByPaymentId({ payment });
  return response.findSwiftDataByPaymentId;
}

export async function findSwiftDataByPaymentIdAndUser(
  paymentId: number,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
) {
  if (!user.currentContract) return;
  const payment = {
    legalPartyId: user.currentContract.orgReferenceId,
    paymentId,
  };
  const response = await sdkPaymentsService.findSwiftDataByPaymentId({ payment });
  return mapPaymentSwiftData(response.findSwiftDataByPaymentId, user, timeRule, keyAvailability);
}

export async function findBudgetPaymentDataByPaymentIdAndUser(
  paymentId: number,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
) {
  if (!user.currentContract) return;
  const payment = {
    legalPartyId: user.currentContract.orgReferenceId,
    paymentId,
  };
  const response = await sdkPaymentsService.findBudgetPaymentDataByPaymentId({ payment });
  return mapPaymentBudgetData(response.findBudgetPaymentDataByPaymentId, user, timeRule, keyAvailability);
}

export async function getInterBankTransferOperations() {
  const operations = await getAllOperations();
  return mapInterBankTransferOperations(operations);
}

export async function getIntraBankOperation() {
  const response = await getAllOperations();
  return mapIntraBankOperation(response);
}

export async function getSwiftOperation() {
  const operations = await getAllOperations();
  return findSwiftOperation(operations);
}

export async function getRko2CardOperations() {
  const operations = await getAllOperations();
  return mapRko2CardOperations(operations);
}

export async function getConversionOperation() {
  const operations = await getAllOperations();
  return findConversionOperation(operations);
}

export async function getBudgetPaymentOperation() {
  const operations = await getAllOperations();
  return findBudgetPaymentOperation(operations);
}

export async function getInterBankOptions() {
  const response = await sdkPaymentsService.getInterBankOptions();
  const operations = mapInterBankTransferOperations(response.getAllOperation);
  const kgs = response.findAllCurrency.find((currency) => currency.isoCode === Currencies.KGS);
  return {
    operations,
    currency: kgs ? { isoNum: String(kgs.id), isoCode: kgs.isoCode } : null,
  };
}

export async function getSwiftOptions() {
  const response = await sdkPaymentsService.getSwiftOptions();
  const operation = findSwiftOperation(response.getAllOperation);
  const countries = excludeRestrictedCountries(response.getAllCountry);
  const exoticCurrencies = response.findAllCurrency;

  return {
    operation,
    countries,
    exoticCurrencies,
  };
}

export async function getRepeatableTransferOptions(
  bankCode: string | null | undefined,
  budgetClassificationCode: string | null | undefined
) {
  let recipientBank: BankFragment | null = null;
  let budgetClassification: BudgetClassificationCodeFragment | null = null;

  if (bankCode && budgetClassificationCode) {
    const response = await sdkPaymentsService.getRepeatableTransferOptions({
      bankCode,
      bankType: BankType.National,
      budgetClassificationCode,
      pageable,
    });
    recipientBank = response.findBankByCodeCriteria.list[0];
    budgetClassification = response.findBudgetClassificationByCodeCriteria.list[0];
  }

  if (bankCode && !budgetClassificationCode) {
    const [bankOption] = await getAccountBicBank(bankCode, BankType.National);
    recipientBank = bankOption;
  }

  return { recipientBank, budgetClassification };
}

export async function getRepeatableSwiftOptions(
  bankCode: string | null | undefined,
  categoryCode: string | null | undefined
) {
  const initialOption: {
    recipientBank: BankFragment | null;
    operationCategory: OperationTypeCategoryFragment | null;
  } = {
    recipientBank: null,
    operationCategory: null,
  };

  if (!bankCode || !categoryCode) {
    return initialOption;
  }

  const response = await sdkPaymentsService.getRepeatableSwiftOptions({
    bankCode,
    bankType: BankType.All,
    categoryCode,
    pageable,
  });

  initialOption.recipientBank = response.findBankByCodeCriteria.list[0];
  initialOption.operationCategory = response.findOperationTypeCategoryByCodeCriteria.list[0];

  return initialOption;
}

export async function calculateExoticAmountInSwift(calculateAmount: CalculateExoticAmountInput) {
  const response = await sdkPaymentsService.calculateExoticAmountInSwift({ calculateAmount });
  return response.calculateExoticAmountInSwift;
}

export async function getAccountBicBank(bankCode: string, bankType: BankType): Promise<BankFragment[]> {
  const response = await sdkPaymentsService.getBanksByCode({ bankCode, bankType, pageable });
  if (response.findBankByCodeCriteria?.list === undefined) return [];
  return response.findBankByCodeCriteria.list;
}

export async function getBanksByCode(bankCode: string, bankType: BankType) {
  const response = await sdkPaymentsService.getBanksByCode({ bankCode, bankType, pageable });
  return mapBanksByCode(response.findBankByCodeCriteria);
}

export async function getBudgetClassificationsByCode(budgetClassificationCode: string) {
  const response = await sdkPaymentsService.getBudgetClassificationsByCode({ budgetClassificationCode, pageable });
  return mapBudgetClassificationByCodeCriteria(response.findBudgetClassificationByCodeCriteria);
}

export async function getOperationTypeCategoryByCode(operationTypeCode: string) {
  const response = await sdkPaymentsService.getOperationTypeCategoryByCode({ operationTypeCode, pageable });
  return mapOperationTypeCategoryByCodeCriteria(response.findOperationTypeCategoryByCodeCriteria);
}

export async function searchPaymentByCriteria(params: SearchPaymentByCriteriaParams) {
  const { searchOperationHistory, user, pageable, transactions = {} } = params;
  const response = await sdkPaymentsService.searchPaymentByCriteria({ searchOperationHistory, pageable });
  const payload = {
    payments: response.searchPaymentByCriteria,
    user,
    transactions,
  };
  return mapPaymentByCriteria(payload);
}

export async function sendPaymentSign(sign: SignatureInput) {
  const response = await sdkPaymentsService.signingPayment({ sign });
  return response.signingPayment;
}

export async function sendPaymentForDelete(paymentId: number, legalPartyId: number) {
  const input = { paymentId, legalPartyId };
  const response = await sdkPaymentsService.deletePayment({ payment: input });
  return response.deletePayment;
}

export async function sendPaymentForSigning(paymentIds: number[], legalPartyId: number) {
  const payment = { paymentIds, legalPartyId };
  const response = await sdkPaymentsService.forSigningPayment({ payment });
  return response.forSigningPayment;
}

export async function getModelProcess(paymentId: number, legalPartyId: number) {
  const payment = { paymentId, legalPartyId };
  const response = await sdkPaymentsService.getModelProcess({ payment });
  return formattedModelProcess(response.getModelProcess);
}

export async function findAllCurrency() {
  const response = await sdkPaymentsService.findAllCurrency();
  return response.findAllCurrency;
}

export async function findStandardCurrencies() {
  return findAllCurrency().then(standardCurrencies);
}

export async function findExoticCurrencies() {
  return findAllCurrency().then(excludeStandardCurrencies);
}

export async function getCurrencyKgs() {
  const response = await findAllCurrency();
  const kgs = response.find((currency) => currency.isoCode === Currencies.KGS);
  return kgs ? { isoNum: String(kgs.id), isoCode: kgs.isoCode } : null;
}

export async function getInfoPaymentStatusHistoryByPaymentId(
  paymentId: number,
  legalPartyId: number,
  statusCode: Status
) {
  const payment = { paymentId, legalPartyId };
  const response = await sdkPaymentsService.getPaymentStatusHistoryByPaymentId({ payment });
  return mapPaymentStatusHistoryByPaymentId(response.getPaymentStatusHistoryByPaymentId, statusCode);
}

export async function getInfoPaymentSignerInfoByPaymentId(paymentId: number) {
  const authUserStore = useAuthUserStore();
  if (!authUserStore?.selectedUserContract?.signScheme) return [];
  const paymentStore = usePaymentStore();
  const payment = {
    legalPartyId: authUserStore.selectedUserContract.orgReferenceId,
    paymentId,
  };
  const response = await sdkPaymentsService.getPaymentSignerInfoByPaymentId({ payment });
  return mapSignatories(
    response.getPaymentSignerInfoByPaymentId,
    authUserStore.selectedUserContract.signScheme,
    paymentStore?.payment?.statusCode
  );
}

export async function getTransferTemplates(transferTemplateCriteria: TransferTemplateCriteriaInput) {
  const response = await sdkPaymentsService.getTransferTemplateByCriteria({ transferTemplateCriteria });
  return response.getTransferTemplateByCriteria;
}

export async function saveTransferTemplate(form: PaymentForm, templateName: string) {
  const template = mapTransferTemplate(form, templateName);
  const response = await sdkPaymentsService.saveTransferTemplate({ template });
  return response.saveTransferTemplate;
}

export async function updateTransferTemplate(form: PaymentForm, templateId: number, templateName: string) {
  const template = mapEditTransferTemplate(form, templateId, templateName);
  const response = await sdkPaymentsService.updateTemplate({ template });
  return response.updateTemplate;
}

export async function deleteTransferTemplate(templateId: number, legalPartyId: number) {
  const template = {
    templateId,
    legalPartyId,
  };
  const response = await sdkPaymentsService.deleteTemplate({ template });
  return response.deleteTemplate;
}
export async function getSecondPersonTokenByPaymentIds(payload: PaymentIdListInput) {
  const response = await sdkPaymentsService.getSecondPersonTokenByPaymentIds({ payments: payload });
  return response.getSecondPersonTokenByPaymentIds;
}

export async function listenPayment(legalPartyId: number, user: User): Promise<Function> {
  const legalPartyIdAndPaymentId = {
    legalPartyId,
    paymentId: null,
  };
  const subscribe = await sdkPaymentsService.listenPayment({ legalPartyIdAndPaymentId });
  // @ts-ignore
  const { unsubscribe } = subscribe((result: ListenPayment) => {
    const payment = result?.data;
    if (!payment) return;
    const res = formatListenPayment(result, user);
    const historyTransactionStore = useHistoryTransactionStore();
    if (res.state === PaymentSubscriptionStateEnum.New)
      return historyTransactionStore.setPayment(res.paymentInformation as OperationHistory);
    if (res.state === PaymentSubscriptionStateEnum.Deleted)
      return historyTransactionStore.deletePayment(res.paymentInformation as OperationHistory);
    historyTransactionStore.updatePayment(res.paymentInformation as OperationHistory);
  });
  return unsubscribe;
}
export async function getCurrencyRates() {
  const response = await sdkPaymentsService.getCurrencyRates();
  return formattedCurrencyRates(response.getCurrencyRates);
}

export async function getCurrencyRatesConversion() {
  const response = await sdkPaymentsService.getCurrencyRatesConversion();
  return response.getCurrencyRates;
}

export async function recallPayment(paymentIdListInput: PaymentIdListInput) {
  const response = await sdkPaymentsService.recallPayment({ paymentIdListInput });
  return response.recallPayment;
}

export async function getNumberOfPayment4Sign(legalPartyIdInput: LegalPartyIdInput) {
  const response = await sdkPaymentsService.getNumberOfPayment4Sign({ legalPartyIdInput });
  return response.getNumberOfPayment4Sign;
}

export async function calculatePaymentCommission(commissionInput: CommissionInput) {
  const response = await sdkPaymentsService.calculateCommission({ commissionInput });
  return response.calculateCommission;
}

export async function refundOperation(refundInput: RefundInput) {
  const response = await sdkPaymentsService.refundOperation({ refundInput });
  return checkSystemError(response.refundOperation.paymentId);
}

export async function findRefundedQrOperationInfoByUser(
  paymentId: number,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
) {
  if (!user.currentContract) return;
  const payment = {
    legalPartyId: user.currentContract.orgReferenceId,
    paymentId,
  };
  const response = await sdkPaymentsService.findRefundedQrOperationInfo({ payment });
  return mapPaymentQrRefundedData(response.findRefundedQrOperationInfo, user, timeRule, keyAvailability);
}

export async function findQrTransferDataByPaymentId(
  paymentId: number,
  user: User,
  timeRule: TimeRuleDto[],
  keyAvailability?: PayControlKeyAvailabilityFragment
) {
  if (!user.currentContract) return;
  const payment = {
    legalPartyId: user.currentContract.orgReferenceId,
    paymentId,
  };
  const response = await sdkPaymentsService.findQrTransferDataByPaymentId({ payment });
  return mapPaymentQrData(response.findQrTransferDataByPaymentId, user, timeRule, keyAvailability);
}

export async function saveBudgetPayment(budgetPayment: BudgetPaymentInput) {
  const response = await sdkPaymentsService.saveBudgetPayment({ budgetPayment });
  return checkSystemError(response.saveBudgetPayment.paymentId);
}

export async function checkIsRefundAvailable(refundIsAvailableInput: RefundIsAvailableInput) {
  const response = await sdkPaymentsService.checkIsRefundAvailable({ refundIsAvailableInput });
  return response.checkIsRefundAvailable;
}

export async function getQrOperationHistory(input: QrOperationFilterInput, pageable: PageInput) {
  const qrOperationFilterInput = mapFilterQrTransaction(input);
  const response = await sdkPaymentsService.getQrOperationHistory({ qrOperationFilterInput, pageable });
  return response.getQrOperationHistory;
}

export async function getQrOperationsLast2Days(filter: QrOperationLast2DaysFilter) {
  const response = await sdkPaymentsService.getQrOperationsLast2Days({ filter });
  return response.getQrOperationsLast2Days;
}

export async function findAllQrTransferByFilter(input: QrTransferFilterInput, pageable: PageInput) {
  const qrTransferFilter = mapFilterQrTransaction(input);
  const response = await sdkPaymentsService.findAllQrTransferByFilter({ qrTransferFilter, pageable });
  return response.findAllQrTransferByFilter;
}

export async function getSalaryDataByLegalPartyId(
  searchSalary: SearchSalary,
  pageable: PageInput,
  signRight: SignRight,
  signType: SignType,
  keyAvailability: PayControlKeyAvailabilityFragment
): Promise<PaginatedSalaryStatement> {
  const response = await sdkPaymentsService.findSalaryDataByLegalPartyId({ searchSalary, pageable });
  return mapSalaries(response.findSalaryDataByLegalPartyId, signRight, signType, keyAvailability);
}

export async function listenSalary(
  salaryIdInput: SalaryIdInput,
  signRight: SignRight,
  signType: SignType,
  keyAvailability: PayControlKeyAvailabilityFragment
): Promise<() => void> {
  const subscribe = await sdkPaymentsService.listenSalary({ salaryIdInput });
  // @ts-ignore
  const { unsubscribe } = subscribe((result: ListenSalaryStatement) => {
    const salaryStatement = result?.data?.listenSalary;
    if (!salaryStatement) return;
    userListenSalaryStatementLine.next({
      data: {
        listenSalary: {
          salaryResponseDto: mapSalary(salaryStatement.salaryResponseDto, signRight, signType, keyAvailability),
          state: salaryStatement.state,
        },
      },
    });
  });
  return unsubscribe;
}

export async function findSalaryDetailBySalaryIdForSign(
  salaryId: SalaryIdInput
): Promise<SalaryInfoWithDetailForSignFragment> {
  const pageable = { amountElements: 1, currentPage: 1 };
  const response = await sdkPaymentsService.findSalaryDetailBySalaryIdForSign({ salaryId, pageable });
  return response.findSalaryDetailBySalaryId;
}

export async function signingSalary(sign: SignatureSalaryInput) {
  const response = await sdkPaymentsService.signingSalary({ sign });
  return response.signingSalary;
}

export async function saveSalary(salaryRequest: SalaryRequest) {
  const response = await sdkPaymentsService.saveSalary({ salaryRequest });
  return response.saveSalary;
}

export async function findSalaryDetailBySalaryId(
  salaryId: SalaryIdInput,
  pageable: PageInput,
  signRight: SignRight,
  signType: SignType,
  keyAvailability: PayControlKeyAvailabilityFragment
): Promise<SalaryInfoWithDetail> {
  const response = await sdkPaymentsService.findSalaryDetailBySalaryId({ salaryId, pageable });
  return mapSalaryDetail(response.findSalaryDetailBySalaryId, signRight, signType, keyAvailability);
}

export async function getPayDaQrOperationInstallment(input: InstallmentFilter, pageable: PageInput) {
  const filter = mapFilterQrOperationInstallment(input);
  const response = await sdkPaymentsService.getQrOperationInstallment({ filter, pageable });
  return mapPayDaInstallmentOperations(response.getQrOperationInstallment);
}

export async function getTotalSumInstallment(input: InstallmentFilter) {
  const filter = mapFilterQrOperationInstallment(input);
  filter.qrOperationStatus = [];
  const response = await sdkPaymentsService.getTotalSumInstallment({ filter });
  return {
    'Pay,Da!': response.getTotalSumInstallment,
  };
}

export async function getPersonalQrOperationHistoryWithPagination(
  input: PersonalQrOperationFilterInput,
  pageable: PageInput
) {
  const personalQrOperationFilterInput = mapFilterQrTransaction(input);
  const response = await sdkPaymentsService.getPersonalQrOperationHistoryWithPagination({
    personalQrOperationFilterInput,
    pageable,
  });
  return response.getPersonalQrOperationHistoryWithPagination;
}
